import { PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { CHART_ZOOM, ZoomValue } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { minMaxAxisDomain } from '@/components/Charts/utils';

export const BUFFER_MULTIPLIER = 0.1;

export const getChartZoomedDomain = ({
  data,
  zoom,
  defaultDomain,
  formatter,
  allowNegativeRange = false,
}: {
  data: GraphFact[];
  zoom: ZoomValue | undefined;
  defaultDomain?: [number, number] | ((args: [number, number]) => [number, number]) | undefined;
  formatter?: (fact: Omit<GraphFact, 'value'> & { value?: number | null }) => number | null | undefined;
  allowNegativeRange?: boolean;
}) => {
  if (!zoom || zoom === CHART_ZOOM.DEFAULT) {
    return defaultDomain ?? minMaxAxisDomain(0.8, 1.2);
  }
  const benchmarkData: number[] = [];
  const timeSeriesData: number[] = [];

  data.forEach((fact) => {
    const isBenchmark = fact.labels?.find((label) =>
      [PlotReportFactLabelNameEnum.benchmark, PlotReportFactLabelNameEnum.benchmark_population_density].includes(
        label.name,
      ),
    );

    const factValue = formatter ? formatter(fact) : fact.value;

    if (isBenchmark) {
      benchmarkData.push(factValue);
    } else {
      timeSeriesData.push(factValue);
    }
  });

  if (zoom === CHART_ZOOM.BENCHMARK && benchmarkData.length > 0) {
    return getDataRange(benchmarkData, allowNegativeRange);
  }

  if (zoom === CHART_ZOOM.TIME_SERIES && timeSeriesData.length > 0) {
    return getDataRange(timeSeriesData, allowNegativeRange);
  }

  return defaultDomain ?? minMaxAxisDomain(0.8, 1.2);
};

const getDataRange = (data: number[], allowNegativeRange: boolean) => {
  const minValue = Math.min(...data);
  const maxValue = Math.max(...data);
  let minLimit =
    minValue - minValue * BUFFER_MULTIPLIER < 0 && !allowNegativeRange ? 0 : minValue - minValue * BUFFER_MULTIPLIER;

  if (minValue < 0 && allowNegativeRange) {
    minLimit = minValue + minValue * BUFFER_MULTIPLIER;
  }

  let maxLimit = maxValue + maxValue * BUFFER_MULTIPLIER;

  if (maxValue < 0 && allowNegativeRange) {
    maxLimit = maxValue - maxValue * BUFFER_MULTIPLIER;
  }
  return [minLimit, maxLimit];
};
